import React, {forwardRef, useCallback, useImperativeHandle, useState} from 'react';
import {Icon, PriorityIcon} from '@priority-software/priority-style-react';
import {SearchDropdownV2} from '../SearchDropdown';
import {Column, formsStore} from '../../../../stores/Form';
import {Option, SearchState, useSearch} from '../../../../hooks/dropdown/useSearch';
import {useSelect} from '../../../../components/Common/FieldSearch/hooks/useSelect';
import {getStyle} from '../../../../components/Common/FieldSearch/utils/dropdownStyle';
import {zoom} from '../../actions/zoom';
import styles from '../../styles/formInputButton.module.scss';
import {DropdownInputType} from '../../../../components/Common/FieldSearch/types/DropdownProps';
import {setMultiSelectValues} from '../../actions/setMultiSelectValues';
import cx from 'classnames';

type Props = {
    getCellWrap: () => HTMLDivElement | null;
    column: Column;
    searchValue: string;
    onSelect: (val: string) => void;
    isAutoComplete?: boolean;
    onClose?: () => void;
    zindex?: number;
    icon?: PriorityIcon;
    dropdownInputType?: DropdownInputType;
    onOpen?: () => void;
    prefetchedResults?: Array<Option>;
};

export type OpenSearchDropdownAPI = {
    closeSearchDropdown: () => void;
    openSearchDropdown: (forceSearch?: boolean) => Promise<void>;
    isOpen: boolean;
};

const buildStateObject = results => {
    return {
        direction: 'ltr',
        focusedIndex: 0,
        hasMore: false,
        isMulti: false,
        isNoResults: false,
        isScrollable: false,
        isSearchable: false,
        isZoom: false,
        results: results
    };
};

export const CellSearchButton = forwardRef((props: Props, ref) => {
    const {
        getCellWrap,
        column,
        searchValue,
        isAutoComplete,
        onSelect,
        onClose,
        dropdownInputType,
        onOpen,
        prefetchedResults
    } = props;
    const [isOpen, setOpen] = useState(false);
    const [dropdownStyles, setDropdownStyles] = useState({});
    const [resultsAutoMaxHeight, setResultsAutoMaxHeight] = useState<number | undefined>();
    const preFetchedSearchState: Partial<SearchState> =
        prefetchedResults && buildStateObject(prefetchedResults);
    const [searchState, searchHandlers] = useSearch(column, null, preFetchedSearchState);

    const {handleSearchOpen} = searchHandlers;
    const closeSearchDropdown = useCallback(() => {
        setDropdownStyles({});
        setOpen(false);
        onClose();
    }, [setOpen, setDropdownStyles, onClose]);

    const onItemSelect = useCallback(
        (value: string) => {
            onSelect(value);
            closeSearchDropdown();
        },
        [closeSearchDropdown, onSelect]
    );

    const onMultiSelect = useCallback(
        async values => {
            closeSearchDropdown();
            if (values.length === 1) {
                onSelect(values[0]);
            } else {
                await setMultiSelectValues(values);
            }
        },
        [closeSearchDropdown, onSelect]
    );

    // select state
    const selectActions = {
        onSelect: onItemSelect,
        onMultiSelect
    };

    const {isItemSelected, selectHandlers} = useSelect(searchState, selectActions);

    const positionDropdown = (elementRect, state) => {
        const {resultsAutoMaxHeight: autoMaxHeight, ...dropdownStyle} = getStyle(
            elementRect,
            true,
            !isAutoComplete,
            state
        );
        setDropdownStyles({
            position: 'fixed',
            zIndex: props.zindex || 10001,
            ...dropdownStyle
        });
        setResultsAutoMaxHeight(autoMaxHeight);
        setOpen(true);
        onOpen && onOpen();
    };

    const openSearchDropdown = useCallback(
        async (forceSearch?: boolean) => {
            const anchorEl = getCellWrap();
            if (anchorEl) {
                const elementRect = anchorEl.getBoundingClientRect();
                const passed =
                    column.parentForm.searchModeLevel === 0
                        ? await formsStore.currentScreen.startRecordEdit(column.parentForm)
                        : true;
                if (passed) {
                    if (!preFetchedSearchState) {
                        await handleSearchOpen(searchValue, forceSearch, false, state => {
                            positionDropdown(elementRect, state);
                        });
                    } else {
                        positionDropdown(elementRect, preFetchedSearchState);
                    }
                }
            }
        },
        [getCellWrap, searchValue, handleSearchOpen, column.parentForm]
    );

    const onZoomToNewEntry = useCallback(async () => {
        await zoom(column.parentForm, column);
    }, [column]);

    const onZoomToAdvancedSearch = useCallback(async () => {
        const zoomForm = await zoom(column.parentForm, column);
        if (zoomForm) {
            zoomForm.setSearchModeLevel(zoomForm.level + 1);
        }
    }, [column]);

    useImperativeHandle(
        ref,
        () => {
            return {
                closeSearchDropdown,
                openSearchDropdown,
                isOpen
            };
        },
        [closeSearchDropdown, openSearchDropdown, isOpen]
    );

    return (
        <>
            <Icon
                componentClasses={cx(styles.button, 'ignore-react-onclickoutside')}
                icon={props.icon ? props.icon : PriorityIcon.arrowBottom}
                onClick={() => openSearchDropdown()}
            />
            <SearchDropdownV2
                isOpen={isOpen}
                onClose={closeSearchDropdown}
                isItemSelected={isItemSelected}
                column={column}
                dropdownInputType={dropdownInputType}
                dropdownStyle={dropdownStyles}
                resultsAutoMaxHeight={resultsAutoMaxHeight}
                onZoomToNewEntry={onZoomToNewEntry}
                onZoomToAdvancedSearch={onZoomToAdvancedSearch}
                {...searchState}
                {...searchHandlers}
                {...selectHandlers}
            />
        </>
    );
});
